/**
 * ProfileCompletenessCard — shown on your own profile until it is complete.
 * Explains what's missing and why it matters for being found and matched.
 *
 * Props:
 *   onImportCV {fn}  opens the CV import dialog (fills most items at once)
 *   refreshKey {any} change it to re-fetch (e.g. after a CV import)
 */
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Paper, CardContent, Typography, Box, LinearProgress, Button, Collapse, Stack,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  RadioButtonUnchecked as TodoIcon,
} from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';

const CV_FILLS = new Set(['skills', 'research_areas', 'education', 'experience', 'output']);

export default function ProfileCompletenessCard({ onImportCV, refreshKey }) {
  const { data, mutate } = useSWR('/user-profiles/me/completeness/', fetcher);
  const [open, setOpen] = useState(true);

  useEffect(() => { if (refreshKey) mutate(); }, [refreshKey, mutate]);

  if (!data || data.score >= 100) return null;
  const missing = data.missing || [];
  const color = data.score >= 70 ? 'success' : data.score >= 40 ? 'warning' : 'error';
  const cvHelps = missing.some((m) => CV_FILLS.has(m.key));

  return (
    <Paper sx={{ mt: 2, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Profile {data.score}% complete</Typography>
          <Button size="small" onClick={() => setOpen((v) => !v)} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
            {open ? 'Hide' : 'Show'}
          </Button>
        </Box>
        <LinearProgress variant="determinate" value={data.score} color={color} sx={{ height: 8, borderRadius: 4 }} />
        <Collapse in={open}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Complete profiles show up in researcher searches and get matched to funding calls.
            These would help most:
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {missing.slice(0, 5).map((m) => (
              <Box key={m.key} sx={{ display: 'flex', gap: 1 }}>
                <TodoIcon sx={{ fontSize: 16, mt: 0.3, color: 'text.disabled' }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>{m.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{m.why}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
          {cvHelps && onImportCV && (
            <Button fullWidth variant="outlined" startIcon={<UploadFileIcon />} sx={{ mt: 2 }} onClick={onImportCV}>
              Fill most of this from your CV
            </Button>
          )}
        </Collapse>
      </CardContent>
    </Paper>
  );
}
